import { Close } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Incident, IncidentReason } from '@silbo/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InadequateFareTransport } from './incidents/InadequateFareTransport';
import { NeedHelp } from './incidents/NeedHelp';
import { PatientMissing } from './incidents/PatientMissing';
import { PatientNotReadyOrOther } from './incidents/PatientNotReadyOrOther';

export type PartialIncident = Partial<Incident> & { reason: IncidentReason };

const reasons: IncidentReason[] = [
  'PATIENT_NOT_READY',
  'INADEQUATE_FARE_TRANSPORT',
  'NEED_HELP',
  'OTHER',
  'PATIENT_MISSING',
];

export function ReportIncidentDialog({
  open,
  onClose,
  onComplete,
  isLoading,
  isOfflineMode,
}: {
  open: boolean;
  onClose: () => void;
  onComplete: (incident: PartialIncident) => void;
  isLoading?: boolean;
  isOfflineMode: boolean;
}) {
  const { t } = useTranslation('patient-transport');

  const [reason, setReason] = useState<IncidentReason>();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [incidentCreated, setIncidentCreated] = useState<PartialIncident>();

  const reset = () => {
    setReason(undefined);
    setIncidentCreated(undefined);
    setConfirmDialogOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h3">{t('reportIncident.title')}</Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {!reason ? (
            <Stack gap={4} marginTop={4}>
              {reasons.map((value) => (
                <Button key={value} variant="outlined" onClick={() => setReason(value)}>
                  {t(`fareIncidents.${value}` as unknown as TemplateStringsArray)}
                </Button>
              ))}
            </Stack>
          ) : reason === 'PATIENT_NOT_READY' ? (
            <PatientNotReadyOrOther
              reason="PATIENT_NOT_READY"
              onBack={reset}
              onComplete={(incident) => {
                setIncidentCreated({ reason: 'PATIENT_NOT_READY', ...incident });
                setConfirmDialogOpen(true);
              }}
            />
          ) : reason === 'INADEQUATE_FARE_TRANSPORT' ? (
            <InadequateFareTransport
              onBack={reset}
              onComplete={(incident) => {
                setIncidentCreated({ reason: 'INADEQUATE_FARE_TRANSPORT', ...incident });
                setConfirmDialogOpen(true);
              }}
            />
          ) : reason === 'NEED_HELP' ? (
            <NeedHelp
              onBack={reset}
              onComplete={(incident) => {
                setIncidentCreated({ reason: 'NEED_HELP', ...incident });
                setConfirmDialogOpen(true);
              }}
            />
          ) : reason === 'OTHER' ? (
            <PatientNotReadyOrOther
              reason="OTHER"
              onBack={reset}
              onComplete={(incident) => {
                setIncidentCreated({ reason: 'OTHER', ...incident });
                setConfirmDialogOpen(true);
              }}
            />
          ) : (
            <PatientMissing
              onBack={reset}
              onComplete={(incident) => {
                setIncidentCreated({ reason: 'PATIENT_MISSING', ...incident });
                setConfirmDialogOpen(true);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>{t('reportIncident.confirm.title')}</DialogTitle>
        {isOfflineMode && (
          <>
            <DialogContent>
              <Typography sx={{ color: 'error.main', fontWeight: 600 }}>
                {t('errors.offlineMode', { ns: 'common' })}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" onClick={onClose}>
                {t('close', { ns: 'common' })}
              </Button>
            </DialogActions>
          </>
        )}
        {!isOfflineMode && (
          <>
            <DialogContent>
              <Typography>
                {t(
                  incidentCreated?.unassignmentRequested
                    ? 'reportIncident.confirm.descriptionWithUnassignment'
                    : 'reportIncident.confirm.descriptionWithoutUnassignment'
                )}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  onComplete(incidentCreated as PartialIncident);
                }}
                disabled={isLoading}
              >
                {t('reportIncident.confirm.yes')}
              </Button>
              <Button variant="outlined" onClick={() => setConfirmDialogOpen(false)} disabled={isLoading}>
                {t('reportIncident.confirm.no')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
